import type { FC } from 'react';
import type { TabsProps } from './Tabs.types';
import Link from 'next/link';
import styles from './Tabs.module.css';
import { Flair, Flex, Text, Pill } from '@/_components';
import { getClasses } from '@/_utils/classnames';

export const Tabs: FC<TabsProps> = ({
  children,
  className,
  isSticky,
  containerClassName,
  tabActions,
  noPadding,
  items,
  itemSize = 'div',
}) => {
  if (!items?.length) return null;

  const tabContainerClasses: string = getClasses(
    styles.tabs,
    containerClassName,
  );
  const tabHeaderClasses: string = getClasses(styles.tabsHeader, className, {
    [styles.tabsHeaderSticky]: isSticky,
  });

  const tabHeaderContainerClasses: string = getClasses(
    styles.tabsHeaderContainer,
    {
      [styles.tabsHeaderContainerNoPadding]: noPadding,
      [styles.tabsHeaderContainerSticky]: isSticky,
    },
  );

  return (
    <div className={tabContainerClasses}>
      <Flex
        isBetween={true}
        isYCentered={true}
        gap={2}
        className={tabHeaderContainerClasses}
      >
        <ol className={tabHeaderClasses} role="tablist">
          {items?.map((item, key) => {
            const tabsHeaderItemClasses: string = getClasses(
              styles.tabsHeaderItem,
              {
                [styles.tabsHeaderItemActive]: item.isActive,
              },
            );

            const renderLink: React.ReactNode = (
              <Link href={item.href!}>
                <Flex isYCentered={true} gap={2}>
                  <Text type={itemSize} fontWeight="semi-bold">
                    {item.title}
                  </Text>
                  {!!item.flair && <Flair count={item.flair} />}
                  {!!item.label && (
                    <Pill intent="info" label={item.label} size="small" />
                  )}
                </Flex>
              </Link>
            );

            const onLinkClick = (event: any) => {
              event.preventDefault();
              return item.onClick?.();
            };

            const renderClick: React.ReactNode = (
              <a onClick={onLinkClick}>
                <Flex isYCentered={true} gap={2}>
                  <Text type={itemSize} fontWeight="semi-bold">
                    {item.title}
                  </Text>
                  {!!item.flair && <Flair count={item.flair} />}
                </Flex>
              </a>
            );

            return (
              <li className={tabsHeaderItemClasses} key={key} role="tab">
                {item.href ? renderLink : renderClick}
              </li>
            );
          })}
        </ol>
        <Flex gap={4} yAlign="baseline" className={styles.tabActions}>
          {tabActions}
        </Flex>
      </Flex>
      {children}
    </div>
  );
};
